import React from 'react';
import ContentManager from './cm/ContentManager.jsx';

import { valueForKeyPath as v } from '../utils.jsx';

import MediaBrowser from './cm/components/mediaBrowser/MediaBrowser.jsx';

class ContentManagerApiService {
  constructor(api_endpoint, access_token, contentspace) {
    this.api_endpoint = api_endpoint;
    this.access_token = access_token;
    this.contentspace = contentspace;
  }

  _fetchMultipartRequest(url, method = 'post', data = null) {
    const init = {
      method: method,
      headers: {
        Authorization: `Bearer ${this.access_token}`,
      },
    };

    init.body = data;

    const final_url = `${this.api_endpoint}/${url}`;
    return fetch(final_url, init)
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        } else {
          var error = new Error(response.statusText);
          error.response = response;
          throw error;
        }
      })
      .catch(error => {
        return error.response.json().then(error_details => {
          error.details = error_details;
          throw error;
        });
      });
  }

  _fetchRequest(url, method = 'get', data = null, headers = null) {
    const init = {
      method: method,
      headers: {
        Authorization: `Bearer ${this.access_token}`,
        'Content-Type': 'application/json',
      },
    };

    if (data) {
      init.body = JSON.stringify(data);
    }

    const final_url = `${this.api_endpoint}/${url}`;
    return fetch(final_url, init)
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        } else {
          var error = new Error(response.statusText);
          error.response = response;
          throw error;
        }
      })
      .catch(error => {
        return error.response.json().then(error_details => {
          error.details = error_details;
          throw error;
        });
      });
  }

  loadArticles(offset = 0, q = null) {
    if (q) return this._fetchRequest(`article?offset=${offset}&q=${q}`);
    return this._fetchRequest(`article?offset=${offset}`);
  }

  loadArticle(articleId) {
    return this._fetchRequest(`article/${articleId}`);
  }

  loadArticlesForSection(sectionId, offset = 0, q) {
    return this._fetchRequest(
      `section/${sectionId}/articles?offset=${offset}&q=${q}`,
    );
  }

  loadSections(parent = null) {
    let url = `section`;
    if (parent) {
      url += `/${parent}/sections`;
    }
    return this._fetchRequest(url);
  }

  loadSection(sectionId) {
    return this._fetchRequest(`section/${sectionId}`);
  }

  createNewSection(section) {
    let method, url;
    if (section._id) {
      method = 'put';
      url = `section/${section._id}`;
    } else {
      method = 'post';
      if (section.parent) {
        url = `section/${section.parent}?treelevel=2`;
      } else {
        url = `section?treelevel=2`;
      }
    }
    return this._fetchRequest(url, method, section);
  }

  deleteSection(sectionId) {
    let method = 'delete';
    let url = `section/${sectionId}`;

    return this._fetchRequest(url, method);
  }

  saveArticle(data, articleId = null) {
    // zavolame request
    let url = `article`;
    let method = 'post';
    if (articleId) {
      url = `article/${articleId}`;
      method = 'put';
    }
    return this._fetchRequest(url, method, data);
  }

  saveArticlePreview(data) {
    // zavolame request
    let method = 'post';
    return this._fetchRequest('public/preview', method, data);
  }

  removeArticle(articleId = null) {
    // zavolame request
    let url = `article/${articleId}`;
    let method = 'delete';
    return this._fetchRequest(url, method);
  }

  bulkDeleteArticles(articleIds = []) {
    // zavolame request na hromadne mazanie
    let url = `article/bulk`;
    let method = 'delete';
    return this._fetchRequest(url, method, { article_ids: articleIds });
  }

  loadDirectories(directoryId = null, filesOffset, filesLimit, withSubdirectories) {
    // zavolame request
    let url = 'storage/directory';
    if (directoryId) {
      url += '/' + directoryId;
    }
    
    url += '?'; 

    if(filesOffset) {
      url += `files_offset=${filesOffset}&`
    }

    if(filesLimit) {
      url += `files_limit=${filesLimit}&`
    }

    url += `with_subdirectories=${withSubdirectories}`

    let method = 'get';
    return this._fetchRequest(url, method);
  }

  createDirectory(data) {
    let url = 'storage/directory';
    let method = 'post';

    return this._fetchRequest(url, method, data);
  }

  deleteDirectory(directoryId) {
    let url = `storage/directory/${directoryId}`;
    let method = 'delete';
    return this._fetchRequest(url, method);
  }

  uploadFile(data) {
    let url = 'storage/file';
    let method = 'post';
    return this._fetchMultipartRequest(url, method, data);
  }

  deleteFile(fileId) {
    let url = `storage/file/${fileId}`;
    let method = 'delete';
    return this._fetchRequest(url, method);
  }

  bulkDelete(data = {}) {
    let url = `storage/bulk`;
    let method = 'delete';
    return this._fetchRequest(url, method, data);
  }

  moveFile(data) {
    let destination_id = data.destinationId;
    let originId = data.originId;

    let url = 'storage/file/' + originId; // + '/move/'+ destination_id;
    let method = 'put';

    let request_data = {
      commands: [{ move: { destination: destination_id } }],
    };

    return this._fetchRequest(url, method, request_data);
  }

  moveFolder(data) {
    let destination_id = data.destinationId;
    let originId = data.originId;

    let url = 'storage/directory/' + originId + '/move/' + destination_id;
    let method = 'post';

    return this._fetchRequest(url, method);
  }

  loadStaticTexts() {
    let method = 'get';
    let url = 'static-content';

    return this._fetchRequest(url, method);
  }

  createStaticText(data) {
    let method = 'post';
    let url = `static-content/${data.cid}`;
    return this._fetchRequest(url, method, data);
  }

  deleteStaticText(textID) {
    let method = 'delete';
    let url = `static-content/${textID}`;

    return this._fetchRequest(url, method);
  }

  updateStaticText(data) {
    let method = 'put';
    let url = `static-content/${data.cid}`;
    return this._fetchRequest(url, method, data);
  }

  getArticleByOtherTextCid(cid) {
    let method = 'get';
    let url = `static-content/${cid}/content`;
    return this._fetchRequest(url, method);
  }

  updateStaticTextContent(data, cid) {
    let method = 'post';
    let url = `static-content/${cid}/content`;

    return this._fetchRequest(url, method, data);
  }

  deleteStaticTextContent(cid) {
    let method = 'delete';
    let url = `static-content/${cid}/content`;

    return this._fetchRequest(url, method);
  }

  loadSectionContent(sectionId) {
    let method = 'get';
    let url = `section/${sectionId}/content`;

    return this._fetchRequest(url, method);
  }

  updateSectionContent(data, sectionId) {
    let method = 'post';
    let url = `section/${sectionId}/content`;

    return this._fetchRequest(url, method, data);
  }

  deleteSectionContent(sectionId) {
    let method = 'delete';
    let url = `section/${sectionId}/content`;

    return this._fetchRequest(url, method);
  }
}

class ContentManagerContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      articlesByQuery: {},
      articles: [],
      articlesPager: {},
      sections: [],
      static_texts: [],
      selectedSectionId: null,
      searchQuery: null,
    };

    this.api_service = new ContentManagerApiService(
      this.props.api_endpoint,
      this.props.access_token,
      this.props.contentspace,
    );
    this.didCreateNewSection = this.didCreateNewSection.bind(this);
    this.loadSections = this.loadSections.bind(this);
    this.loadSection = this.loadSection.bind(this);
    this.loadArticles = this.loadArticles.bind(this);
    this.saveArticle = this.saveArticle.bind(this);
    this.saveArticlePreview = this.saveArticlePreview.bind(this);
    this.removeArticle = this.removeArticle.bind(this);
    this.loadArticle = this.loadArticle.bind(this);
    this.loadDirectories = this.loadDirectories.bind(this);
    this.createDirectory = this.createDirectory.bind(this);
    this.deleteDirectory = this.deleteDirectory.bind(this);
    this.deleteFile = this.deleteFile.bind(this);
    this.uploadFile = this.uploadFile.bind(this);
    this.moveFile = this.moveFile.bind(this);
    this.moveFolder = this.moveFolder.bind(this);
    this.onTreeMenuItemClick = this.onTreeMenuItemClick.bind(this);
    this.didDeleteSection = this.didDeleteSection.bind(this);
    this.createStaticText = this.createStaticText.bind(this);
    this.deleteStaticText = this.deleteStaticText.bind(this);
    this.loadStaticTexts = this.loadStaticTexts.bind(this);
    this.updateStaticText = this.updateStaticText.bind(this);
    this.onStaticTextClick = this.onStaticTextClick.bind(this);
    this.onUpdateStaticTextContent = this.onUpdateStaticTextContent.bind(this);
    this.onDeleteStaticTextContent = this.onDeleteStaticTextContent.bind(this);
    this.loadSectionContent = this.loadSectionContent.bind(this);
    this.updateSectionContent = this.updateSectionContent.bind(this);
    this.deleteSectionContent = this.deleteSectionContent.bind(this);
    this.pagerDidChange = this.pagerDidChange.bind(this);
    this.handleChangeSearchParams = this.handleChangeSearchParams.bind(this);
    this.bulkDeleteArticles = this.bulkDeleteArticles.bind(this);
    this.bulkDelete = this.bulkDelete.bind(this);
  }

  handleChangeSearchParams(q) {
    this.setState(
      {
        searchQuery: q && q !== '' ? q : null,
        selectedSectionId: null,
      },
      () => {
        this.loadArticles(null, 0, q);
      },
    );
  }

  componentDidMount() {
    // ked sa nam namontoval
    this.loadAll(false);
  }

  loadAll(clear = true) {
    if (clear) {
      this.setState({
        articles: [],
        articlesByQuery: {},
        sections: [],
        static_texts: [],
      });
    }
    this.loadArticles();
    this.loadSections();
    this.loadStaticTexts();
  }

  loadArticles(sectionId = null, offset = 0, query = null) {
    this.setState({ articlesIsLoading: true });
    // nacitame zoznam clankov
    // ak nemame zaznacenu sekciu
    if (!sectionId) {
      this.api_service
        .loadArticles(offset, query)
        .then(response => {
          if (offset === 0) {
            this.setState({
              articlesIsLoading: false,
              articlesByQuery: {
                ...this.state.articlesByQuery,
                [query]: {
                  articles: response.articles,
                  articlesPager: {
                    limit: response['limit'],
                    offset: response['offset'],
                    total: response['total'],
                  },
                },
              },
            });
          } else {
            this.setState({
              articlesIsLoading: false,
              articlesByQuery: {
                ...this.state.articlesByQuery,
                [query]: {
                  articles: response.articles,
                  articlesPager: {
                    limit: response['limit'],
                    offset: response['offset'],
                    total: response['total'],
                  },
                },
              },
            });
          }
        })
        .catch(error => {
          this.setState({
            articlesIsLoading: false,
            articlesByQuery: {
              ...this.state.articlesByQuery,
              [query]: {
                ...this.state.articlesByQuery[query],
              },
            },
          });
          console.log(error);
          alert(error.details.description);
        });
    }
    // ak mame zaznacenu sekciu
    else {
      this.api_service
        .loadArticlesForSection(sectionId, offset, query)
        .then(articles => {
          this.setState({ articlesIsLoading: false });
          this.setState({
            articles: articles['articles']['articles'],
            articlesPager: {
              limit: articles['articles']['limit'],
              offset: articles['articles']['offset'],
              total: articles['articles']['total'],
            },
          });
        })
        .catch(error => {
          this.setState({ articlesIsLoading: false });
          console.log(error);
          alert(error.details.description);
        });
    }
  }

  pagerDidChange(offset) {
    this.loadArticles(
      this.state.selectedSectionId,
      offset,
      this.state.searchQuery,
    );
    // alert("Load articles with new offset " + offset);
  }

  onTreeMenuItemClick(item) {
    this.setState({
      articlesIsLoading: true,
      selectedSectionId: item._id,
      searchQuery: null,
    });
    return this.loadArticles(item._id, 0);
  }

  loadArticle(articleId) {
    // nacitame zoznam clankov
    return this.api_service.loadArticle(articleId);
  }

  loadSections(parent_id = null) {
    // nacitame zoznam clankov
    this.api_service
      .loadSections(parent_id)
      .then(sections => {
        if (!parent_id) {
          this.setState({ sections: sections['sections'] });
        } else {
          let stateSections = this.state.sections.slice();
          this.setSections(stateSections, sections, parent_id);
          this.setState({ sections: stateSections });
        }
      })
      .catch(error => {
        console.log(error);
        alert(error.details.description);
      });
  }

  loadSection(sectionId) {
    return this.api_service.loadSection(sectionId);
  }

  setSections(stateSections, retrievedSections, parent_id) {
    stateSections.forEach(element => {
      if (element.sections) {
        this.setSections(element.sections, retrievedSections, parent_id);
      }
      if (element._id === parent_id) {
        element.sections = retrievedSections['sections'];
      }
    });
  }

  didCreateNewSection(data) {
    return this.api_service.createNewSection(data);
  }

  didDeleteSection(sectionId) {
    return this.api_service.deleteSection(sectionId);
  }

  saveArticle(article, articleId = null) {
    const articles = this.state.articles;
    if (!articleId) {
      articles.unshift(article);
      this.setState({ articles: articles });
    } else {
      // updatneme z aktualneho zoznamu
      for (const a in articles) {
        if (articles[a]._id === articleId) {
          articles[a] = article;
          this.setState({ articles: articles });
          break;
        }
      }
    }

    return this.api_service.saveArticle(article, articleId);
  }

  removeArticle(articleId) {
    const articles = this.state.articles;
    // updatneme z aktualneho zoznamu
    for (const a in articles) {
      if (articles[a]._id === articleId) {
        articles.splice(a, 1);
        this.setState({ articles: articles });
        break;
      }
    }

    return this.api_service.removeArticle(articleId);
  }

  bulkDeleteArticles(articleIds) {
    return this.api_service.bulkDeleteArticles(articleIds).then(response => {
      this.loadArticles(this.state.selectedSectionId, 0, this.state.searchQuery);
      return response;
    });
  }

  saveArticlePreview(article) {
    return this.api_service.saveArticlePreview(article);
  }

  // Content manager
  loadDirectories(directory_id = 'null', filesOffset, filesLimit, withSubdirectories = 1) {
    if (directory_id === '/') {
      directory_id = 'null';
    }
    return this.api_service.loadDirectories(directory_id, filesOffset, filesLimit, withSubdirectories);
  }

  createDirectory(data) {
    return this.api_service.createDirectory(data);
  }

  deleteDirectory(directory_id) {
    return this.api_service.deleteDirectory(directory_id);
  }

  deleteFile(file_id) {
    return this.api_service.deleteFile(file_id);
  }

  uploadFile(data) {
    return this.api_service.uploadFile(data);
  }

  moveFile(data) {
    return this.api_service.moveFile(data);
  }

  moveFolder(data) {
    return this.api_service.moveFolder(data);
  }

  bulkDelete(data) {
    return this.api_service.bulkDelete(data);
  }

  createStaticText(data) {
    return this.api_service.createStaticText(data);
  }

  updateStaticText(data) {
    return this.api_service.updateStaticText(data);
  }

  deleteStaticText(textID) {
    return this.api_service.deleteStaticText(textID);
  }

  loadStaticTexts() {
    this.api_service.loadStaticTexts().then(staticTexts => {
      this.setState({
        static_texts: staticTexts['static_content'],
      }),
        exception => {
          alert(exception);
        };
    });
  }

  onStaticTextClick(cid) {
    return this.api_service.getArticleByOtherTextCid(cid);
  }

  onUpdateStaticTextContent(data, cid) {
    return this.api_service.updateStaticTextContent(data, cid);
  }

  onDeleteStaticTextContent(cid) {
    return this.api_service.deleteStaticTextContent(cid);
  }

  loadSectionContent(sectionId) {
    return this.api_service.loadSectionContent(sectionId);
  }

  updateSectionContent(data, sectionId) {
    return this.api_service.updateSectionContent(data, sectionId);
  }

  deleteSectionContent(sectionId) {
    return this.api_service.deleteSectionContent(sectionId);
  }

  onFileSelect(url) {
    function getUrlParam(paramName) {
      let reParam = new RegExp('(?:[?&]|&)' + paramName + '=([^&]+)', 'i');
      let match = window.location.search.match(reParam);
      return match && match.length > 1 ? match[1] : null;
    }
    let baseUri = 'http://data.tvkosice.sk/cmdata/';
    let funcNum = getUrlParam('CKEditorFuncNum');
    window.opener.CKEDITOR.tools.callFunction(funcNum, baseUri + url);
    window.close();
  }

  render() {
    if (this.props.isMediaBrowser) {
      return (
        <MediaBrowser
          multipleSelect={false}
          onFileSelect={this.onFileSelect}
          mediaManager={{
            loadDirectories: this.loadDirectories,
            createDirectory: this.createDirectory,
            deleteDirectory: this.deleteDirectory,
            deleteFile: this.deleteFile,
            uploadFile: this.uploadFile,
            moveFile: this.moveFile,
            moveFolder: this.moveFolder,
            bulkDelete: this.bulkDelete,
          }}
        />
      );
    } else {
      return (
        <ContentManager
          contentspace_info={this.props.contentspace_info}
          articles={
            this.state.selectedSectionId
              ? this.state.articles
              : v(
                  this.state.articlesByQuery,
                  `${
                    this.state.searchQuery ? this.state.searchQuery : 'null'
                  }.articles`,
                  [],
                )
          }
          articlesIsLoading={this.state.articlesIsLoading}
          articlesPager={
            this.state.selectedSectionId
              ? this.state.articlesPager
              : v(
                  this.state.articlesByQuery,
                  `${
                    this.state.searchQuery ? this.state.searchQuery : 'null'
                  }.articlesPager`,
                  [],
                )
          }
          pagerDidChange={this.pagerDidChange}
          onChangeQueryParams={this.handleChangeSearchParams}
          reloadArticles={this.loadArticles}
          saveArticle={this.saveArticle}
          saveArticlePreview={this.saveArticlePreview}
          removeArticle={this.removeArticle}
          loadArticle={this.loadArticle}
          loadSection={this.loadSection}
          sections={this.state.sections}
          reloadSections={this.loadSections}
          static_texts={this.state.static_texts}
          didCreateNewSection={this.didCreateNewSection}
          mediaManager={{
            loadDirectories: this.loadDirectories,
            createDirectory: this.createDirectory,
            deleteDirectory: this.deleteDirectory,
            deleteFile: this.deleteFile,
            uploadFile: this.uploadFile,
            moveFile: this.moveFile,
            moveFolder: this.moveFolder,
            bulkDelete: this.bulkDelete,
          }}
          onTreeMenuItemClick={this.onTreeMenuItemClick}
          didDeleteSection={this.didDeleteSection}
          createStaticText={this.createStaticText}
          updateStaticText={this.updateStaticText}
          deleteStaticText={this.deleteStaticText}
          reloadStaticTexts={this.loadStaticTexts}
          onStaticTextClick={this.onStaticTextClick}
          onUpdateStaticTextContent={this.onUpdateStaticTextContent}
          onDeleteStaticTextContent={this.onDeleteStaticTextContent}
          loadSectionContent={this.loadSectionContent}
          onUpdateSectionContent={this.updateSectionContent}
          onDeleteSectionContent={this.deleteSectionContent}
          defaultAuthorName={this.props.defaultAuthorName}
          previewUrl={this.props.previewUrl}
          bulkDeleteArticles={this.bulkDeleteArticles}
        />
      );
    }
  }
}

ContentManagerContainer.defaultProps = {
  access_token: null,
  api_endpoint: '../api/cm',
};

export default ContentManagerContainer;



// WEBPACK FOOTER //
// ./src/js/app/ContentManagerContainer.jsx